import { PropsWithChildren } from "react";
import { Empty, theme } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { EntityTypes } from "@vzbl/integrations";
import { CssVars } from "src/components/CssVars";
import { useEntityContext } from "./useEntityContext";
import { BranchEntity } from "./v2/BranchEntity";
import { CalendarEntryEntity } from "./v2/CalendarEntryEntity";
import { ChannelEntity } from "./v2/ChannelEntity";
import { CicdPipelineEntity } from "./v2/CicdPipelineEntity";
import { CommitEntity } from "./v2/CommitEntity";
import { DefaultEntity } from "./v2/DefaultEntity";
import { DiscussionCommentEntity } from "./v2/DiscussionCommentEntity";
import { FlowEntity } from "./v2/FlowEntity";
import { IssueCommentEntity } from "./v2/IssueCommentEntity";
import { IssueEntity as IssueEntityV2 } from "./v2/IssueEntity";
import { MessageEntity } from "./v2/MessageEntity";
import { PersonEntity as PersonEntityV2 } from "./v2/PersonEntity";
import { PullRequestEntity } from "./v2/PullRequestEntity";
import { PullRequestReviewCommentEntity } from "./v2/PullRequestReviewCommentEntity";
import { RepositoryEntity } from "./v2/RepositoryEntity";
import { SourceFileEntity } from "./v2/SourceFileEntity";
import { SprintEntity } from "./v2/SprintEntity";
import { WorklogEntity } from "./v2/WorklogEntity";
import { PullRequestReviewEntity } from "./v2/PullRequestReviewEntity";
import { TimeinStatusEntity } from "./v2/TimeInStatusEntity";
import { ProjectEntity } from "./v2/ProjectEntity";

import { IssueEntity as IssueEntityV3 } from "./v3/IssueEntity";
import { PullRequestEntity as PullRequestEntityV3 } from "./v3/PullRequestEntity";
import { useEffectOnce } from "usehooks-ts";
import { useApplicationContext } from "src/hooks/useApplication";

// Feature flag to control which version of the entity UI to show
const USE_V3 = (ctx: ReturnType<typeof useApplicationContext>) =>
  ctx.user?.superuser === true;

export function EntityDomain(props: {
  openEntity: ((uuid: string) => void) | undefined;
  square?: boolean;
  syncAppTitle?: boolean;
}) {
  const { query, dataRef } = useEntityContext();
  const placeholder = !query.isFetched || !dataRef.current;
  return (
    <EntityWrapper square={props.square} placeholder={placeholder}>
      {placeholder ? (
        query.isFetched ? (
          <Empty />
        ) : (
          <LoadingOutlined />
        )
      ) : (
        <EntityDomainImpl syncAppTitle={props.syncAppTitle} />
      )}
    </EntityWrapper>
  );
}

function EntityDomainImpl(props: { syncAppTitle?: boolean }) {
  const ctx = useEntityContext();
  const app = useApplicationContext();
  const entity = ctx.dataRef.current.entity;
  const isV3Enabled = USE_V3(app);

  useEffectOnce(() => {
    if (props.syncAppTitle) {
      const title = entity?.properties?.name || `Entity #${entity.uuid}`;
      app.setTitle(title);
    }
  });

  if (!entity) {
    console.warn("missing entity", { entityUuid: ctx.entityUuid });
    return <div>not found</div>;
  }

  const entityType = entity.labels?.at(0) || "";
  if (!entityType) {
    console.warn("missing entity type", { entityUuid: ctx.entityUuid, entity });
    return <div>unexpected data</div>;
  }

  switch (entityType) {
    case EntityTypes.Branch: {
      return <BranchEntity />;
    }
    case EntityTypes.CalendarEntry: {
      return <CalendarEntryEntity />;
    }
    case EntityTypes.CicdPipeline: {
      return <CicdPipelineEntity />;
    }
    case EntityTypes.Channel: {
      return <ChannelEntity />;
    }
    case EntityTypes.Commit: {
      return <CommitEntity />;
    }
    case EntityTypes.DiscussionComment: {
      return <DiscussionCommentEntity />;
    }
    case EntityTypes.Flow: {
      return <FlowEntity />;
    }
    case EntityTypes.Issue: {
      return isV3Enabled ? <IssueEntityV3 /> : <IssueEntityV2 />;
    }
    case EntityTypes.IssueComment: {
      return <IssueCommentEntity />;
    }
    case EntityTypes.Message: {
      return <MessageEntity />;
    }
    case EntityTypes.Person: {
      return <PersonEntityV2 />;
    }
    case EntityTypes.Project: {
      return <ProjectEntity />;
    }
    case EntityTypes.PullRequest: {
      return isV3Enabled ? <PullRequestEntityV3 /> : <PullRequestEntity />;
    }
    case EntityTypes.PullRequestReview: {
      return <PullRequestReviewEntity />;
    }
    case EntityTypes.PullRequestReviewComment: {
      return <PullRequestReviewCommentEntity />;
    }
    case EntityTypes.Repository: {
      return <RepositoryEntity />;
    }
    case EntityTypes.SourceFile: {
      return <SourceFileEntity />;
    }
    case EntityTypes.Sprint: {
      return <SprintEntity />;
    }
    case EntityTypes.TimeInStatus: {
      return <TimeinStatusEntity />;
    }
    case EntityTypes.Worklog: {
      return <WorklogEntity />;
    }
    default: {
      return <DefaultEntity />;
    }
  }
}

function EntityWrapper(
  props: PropsWithChildren<{ placeholder?: boolean; square?: boolean }>
) {
  const { token } = theme.useToken();
  return (
    <CssVars>
      <div
        style={{
          backgroundColor: token.colorBgContainer,
          borderRadius: props.square ? 0 : 16,
          height: "100%",
          width: "100%",
          overflow: "auto",
          display: props.placeholder ? "flex" : undefined,
          justifyContent: props.placeholder ? "center" : undefined,
          alignItems: props.placeholder ? "center" : undefined,
        }}
      >
        {props.children}
      </div>
    </CssVars>
  );
}
